import { supabase } from '../supabaseClient/supabaseClient';
import { detectDevice, getBrowserInfo } from './utils/deviceDetection';

/**
 * Session Metadata Service
 * Manages ephemeral session context that adapts to user's environment
 * Component-specific metadata for Tasks, Reflections, Courses, Projects
 */
class SessionMetadataService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.metadata = {};
    this.startTime = Date.now();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize session metadata for a component
   * @param {string} componentType - 'task', 'reflection', 'course', 'project'
   * @param {string} userId - User ID
   */
  async initializeSession(componentType, userId) {
    const deviceInfo = this.collectDeviceInfo();
    const usagePatterns = await this.getUsagePatterns(userId, componentType);
    const componentStats = await this.getComponentStats(userId, componentType);

    this.metadata[componentType] = {
      sessionId: this.sessionId,
      componentType,
      deviceInfo,
      usagePatterns,
      componentStats,
      startedAt: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
    };

    // Store in Supabase for persistence
    await this.persistSessionMetadata(userId, componentType);

    return this.metadata[componentType];
  }

  /**
   * Collect device and browser information
   */
  collectDeviceInfo() {
    const device = detectDevice();
    const browser = getBrowserInfo();

    return {
      deviceType: device.type, // 'desktop', 'mobile', 'tablet'
      browser: browser.name,
      browserVersion: browser.version,
      os: browser.os,
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}×${window.screen.height}`,
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio,
      darkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
      jsEnabled: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
    };
  }

  /**
   * Get usage patterns from database
   * @param {string} userId
   * @param {string} componentType
   */
  async getUsagePatterns(userId, componentType) {
    try {
      // Query activity patterns from the database
      const { data: recentActivity } = await supabase
        .from('ml_session_metadata')
        .select('started_at, last_activity, component_stats')
        .eq('user_id', userId)
        .eq('component_type', componentType)
        .gte('started_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .order('started_at', { ascending: false });

      if (!recentActivity || recentActivity.length === 0) {
        return this.getDefaultUsagePatterns();
      }

      // Calculate activity metrics
      const now = Date.now();
      const oneDayAgo = now - 24 * 60 * 60 * 1000;
      const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
      const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

      const activeDaysLast1 = this.countActiveDays(recentActivity, oneDayAgo);
      const activeDaysLast7 = this.countActiveDays(recentActivity, sevenDaysAgo);
      const activeDaysLast30 = this.countActiveDays(recentActivity, thirtyDaysAgo);

      return {
        activeDaysLast1,
        activeDaysLast7,
        activeDaysLast30,
        totalSessions: recentActivity.length,
        averageSessionDuration: this.calculateAverageSessionDuration(recentActivity),
        lastActiveAt: recentActivity[0]?.last_activity,
      };
    } catch (error) {
      void error;
      return this.getDefaultUsagePatterns();
    }
  }

  /**
   * Get component-specific statistics
   * @param {string} userId
   * @param {string} componentType
   */
  async getComponentStats(userId, componentType) {
    try {
      switch (componentType) {
        case 'task':
          return await this.getTaskStats(userId);
        case 'reflection':
          return await this.getReflectionStats(userId);
        case 'course':
          return await this.getCourseStats(userId);
        case 'project':
          return await this.getProjectStats(userId);
        default:
          return {};
      }
    } catch (error) {
      void error;
      return {};
    }
  }

  /**
   * Get task-specific statistics
   */
  async getTaskStats(userId) {
    const { data: tasks } = await supabase
      .from('ml_tasks_staging')
      .select('completed, task_urgency_level, estimated_minutes, time_spent')
      .eq('user_id', userId)
      .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

    if (!tasks || tasks.length === 0) return {};

    const completed = tasks.filter((t) => t.completed).length;
    const total = tasks.length;
    const completionRate = total > 0 ? (completed / total) * 100 : 0;

    const urgencyDistribution = tasks.reduce((acc, task) => {
      const level = task.task_urgency_level || 'medium';
      acc[level] = (acc[level] || 0) + 1;
      return acc;
    }, {});

    return {
      totalTasks: total,
      completedTasks: completed,
      completionRate: completionRate.toFixed(1),
      urgencyDistribution,
      averageEstimatedMinutes:
        tasks.reduce((sum, t) => sum + (t.estimated_minutes || 0), 0) / tasks.length,
      averageTimeSpent: tasks.reduce((sum, t) => sum + (t.time_spent || 0), 0) / tasks.length,
    };
  }

  /**
   * Get reflection-specific statistics
   */
  async getReflectionStats(userId) {
    const { data: reflections } = await supabase
      .from('ml_reflections')
      .select('created_at, reflection_entry')
      .eq('user_id', userId)
      .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

    if (!reflections || reflections.length === 0) return {};

    return {
      totalReflections: reflections.length,
      averageReflectionLength:
        reflections.reduce((sum, r) => sum + (r.reflection_entry?.length || 0), 0) /
        reflections.length,
      reflectionFrequency: reflections.length / 30, // per day
      lastReflectionAt: reflections[0]?.created_at,
    };
  }

  /**
   * Get course-specific statistics
   */
  async getCourseStats(userId) {
    const { data: enrollments } = await supabase
      .from('ml_course_enrollments')
      .select('course_id, progress, last_accessed_at')
      .eq('user_id', userId);

    if (!enrollments || enrollments.length === 0) return {};

    return {
      totalCourses: enrollments.length,
      averageProgress:
        enrollments.reduce((sum, e) => sum + (e.progress || 0), 0) / enrollments.length,
      activeCourses: enrollments.filter((e) => e.progress > 0 && e.progress < 100).length,
      completedCourses: enrollments.filter((e) => e.progress === 100).length,
    };
  }

  /**
   * Get project-specific statistics
   */
  async getProjectStats(userId) {
    const { data: projects } = await supabase
      .from('ml_projects')
      .select('id, status, created_at')
      .eq('user_id', userId);

    if (!projects || projects.length === 0) return {};

    return {
      totalProjects: projects.length,
      activeProjects: projects.filter((p) => p.status === 'in_progress').length,
      completedProjects: projects.filter((p) => p.status === 'completed').length,
    };
  }

  /**
   * Update session activity timestamp
   */
  updateActivity(componentType) {
    if (this.metadata[componentType]) {
      this.metadata[componentType].lastActivity = new Date().toISOString();
      this.metadata[componentType].sessionDuration = Math.floor(
        (Date.now() - this.startTime) / 1000
      );
    }
  }

  /**
   * Get current session metadata for a component
   */
  getMetadata(componentType) {
    return this.metadata[componentType] || null;
  }

  /**
   * Persist session metadata to Supabase
   */
  async persistSessionMetadata(userId, componentType) {
    const metadata = this.metadata[componentType];
    if (!metadata) return;

    try {
      await supabase.from('ml_session_metadata').upsert({
        user_id: userId,
        component_type: componentType,
        session_id: this.sessionId,
        device_info: metadata.deviceInfo,
        usage_patterns: metadata.usagePatterns,
        component_stats: metadata.componentStats,
        started_at: metadata.startedAt,
        last_activity: metadata.lastActivity,
      });
    } catch (error) {
      void error;
    }
  }

  /**
   * Helper: Count active days in a time range
   */
  countActiveDays(activities, sinceTimestamp) {
    const uniqueDays = new Set();
    activities.forEach((activity) => {
      const activityTime = new Date(activity.started_at).getTime();
      if (activityTime >= sinceTimestamp) {
        const day = new Date(activityTime).toDateString();
        uniqueDays.add(day);
      }
    });
    return uniqueDays.size;
  }

  /**
   * Helper: Calculate average session duration
   */
  calculateAverageSessionDuration(activities) {
    if (activities.length === 0) return 0;

    const durations = activities
      .map((activity) => {
        const start = new Date(activity.started_at).getTime();
        const end = new Date(activity.last_activity).getTime();
        return (end - start) / 1000; // seconds
      })
      .filter((d) => d > 0);

    return durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
  }

  /**
   * Default usage patterns for new users
   */
  getDefaultUsagePatterns() {
    return {
      activeDaysLast1: 1,
      activeDaysLast7: 1,
      activeDaysLast30: 1,
      totalSessions: 1,
      averageSessionDuration: 0,
      lastActiveAt: new Date().toISOString(),
    };
  }
}

// Singleton instance
const sessionMetadataService = new SessionMetadataService();

export default sessionMetadataService;
